// src/components/heygen/HeyGenTranslatePage.tsx
// Video translation — pick video URL, target languages, mode, then poll.

import { useCallback, useEffect, useState } from 'react';
import { Globe, Loader2 } from 'lucide-react';

import {
  ApiError, listLanguages, translateVideo, checkJob,
  type HeyGenLanguage, type HeyGenJob,
} from '@/lib/api_heygen';
import { useJobPoller } from '@/hooks/useJobPoller';
import { VideoPlayer } from '@/components/shared/VideoPlayer';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';
import { ApiKeyMissing } from '@/components/shared/ApiKeyMissing';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => void;

export function HeyGenTranslatePage({ addNotification }: { addNotification?: Notifier }) {
  const [languages, setLanguages] = useState<HeyGenLanguage[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiMissing, setApiMissing] = useState(false);

  // Form
  const [videoUrl, setVideoUrl] = useState('');
  const [targetLangs, setTargetLangs] = useState<string[]>([]);
  const [mode, setMode] = useState<'fast' | 'quality'>('quality');
  const [submitting, setSubmitting] = useState(false);

  // Job
  const [jobId, setJobId] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);

  const loadMeta = useCallback(async () => {
    setLoading(true);
    setApiMissing(false);
    try {
      const langs = await listLanguages();
      setLanguages(langs);
    } catch (e: any) {
      if (e instanceof ApiError && (e.status === 403 || e.status === 502)) setApiMissing(true);
      else addNotification?.({ type: 'error', title: 'Error cargando idiomas', message: e.message });
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadMeta(); }, [loadMeta]);

  const poller = useJobPoller<HeyGenJob>({
    checkFn: checkJob,
    jobId,
    interval: 4000,
    onComplete: (r: HeyGenJob) => {
      setResultUrl(r.video_url || null);
      addNotification?.({ type: 'success', title: 'Traducción completada' });
    },
    onFail: (r: HeyGenJob) => {
      addNotification?.({ type: 'error', title: 'Error en traducción', message: r.error_message || 'Failed' });
    },
  });

  const toggleLang = (code: string) => {
    setTargetLangs((prev) =>
      prev.includes(code) ? prev.filter((l) => l !== code) : [...prev, code],
    );
  };

  const handleSubmit = async () => {
    if (!videoUrl.trim() || !targetLangs.length) return;
    setSubmitting(true);
    setResultUrl(null);
    setJobId(null);
    try {
      const job = await translateVideo({
        video_url: videoUrl.trim(),
        target_languages: targetLangs,
        mode,
      });
      setJobId(job.id);
    } catch (e: any) {
      addNotification?.({ type: 'error', title: 'Error', message: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (apiMissing) return <ApiKeyMissing service="HeyGen" />;
  if (loading) return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-white/40" /></div>;

  const isPolling = poller.status === 'polling';

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Video URL */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">URL del video a traducir</label>
        <input
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--acc1)]"
          placeholder="https://cdn.example.com/video.mp4"
        />
      </div>

      {/* Language selector */}
      <div>
        <label className="text-xs text-white/50 mb-2 block">Idiomas destino</label>
        <div className="flex flex-wrap gap-2">
          {languages.map((l) => (
            <button
              key={l.code}
              onClick={() => toggleLang(l.code)}
              className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                targetLangs.includes(l.code)
                  ? 'bg-[var(--acc1)]/20 border-[var(--acc1)]/50 text-[var(--acc1)]'
                  : 'bg-white/5 border-white/10 text-white/50 hover:border-white/20'
              }`}
            >
              {l.language}
            </button>
          ))}
        </div>
        {targetLangs.length > 0 && (
          <p className="text-[11px] text-white/40 mt-1">{targetLangs.length} idioma(s) seleccionado(s)</p>
        )}
      </div>

      {/* Mode */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">Modo</label>
        <div className="flex gap-2">
          {(['fast', 'quality'] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`px-4 py-2 text-sm rounded-lg font-medium transition-all ${
                mode === m ? 'bg-[var(--acc1)] text-white shadow' : 'bg-white/5 text-white/50 hover:text-white/80'
              }`}
            >
              {m === 'fast' ? 'Rápido' : 'Alta calidad'}
            </button>
          ))}
        </div>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={submitting || isPolling || !videoUrl.trim() || !targetLangs.length}
        className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[var(--acc1)] text-white font-medium hover:opacity-90 disabled:opacity-40 transition-opacity"
      >
        {submitting || isPolling ? <Loader2 size={16} className="animate-spin" /> : <Globe size={16} />}
        {isPolling ? 'Traduciendo…' : 'Traducir video'}
      </button>

      {/* Polling status */}
      {jobId && poller.status !== 'idle' && (
        <div className="o-card flex items-center gap-3">
          <JobStatusBadge status={poller.data?.status || 'pending'} />
          <span className="text-sm text-white/60">
            {poller.status === 'polling' && 'Traduciendo video…'}
            {poller.status === 'completed' && 'Traducción lista'}
            {poller.status === 'failed' && (poller.error || 'Error')}
          </span>
        </div>
      )}

      {resultUrl && (
        <div className="o-card border border-emerald-500/30">
          <VideoPlayer src={resultUrl} />
        </div>
      )}
    </div>
  );
}
